/**
 * Shared helpers for managing system-level connections.
 * Used by both the admin settings UI and the desktop event handler
 * to ensure consistent add/remove logic.
 */

import { getOpenAIConfig, updateOpenAIConfig } from '$lib/apis/openai';
import { getTerminalServerConnections, setTerminalServerConnections } from '$lib/apis/configs';

// ─── OpenAI Connections ─────────────────────────────────

/**
 * Connection order is routing priority: when the same model ID is listed by
 * several connections, `open_webui.routers.openai.get_all_models()` keeps the
 * first *enabled* connection that actually returned the ID (disabled
 * connections, failed model probes and manual `model_ids` allow-lists can all
 * skip a connection). Exposing the order in the admin UI is what lets an
 * operator put a direct provider (e.g. token-plan-cn.xiaomimimo.com) ahead of
 * a relay that re-serves the same IDs (e.g. teamorouter.cn).
 */
export type OpenAIOrderState = {
	urls: string[];
	keys: string[];
	configs: Record<string, any>;
};

const openAIConfigForIndex = (state: OpenAIOrderState, idx: number) => {
	const configs = state.configs ?? {};
	// Legacy connections key their config by URL instead of by numeric index.
	return configs[String(idx)] ?? configs[state.urls[idx]] ?? {};
};

/**
 * Move one OpenAI-compatible connection to another position, keeping its key
 * and its per-connection config attached to the same URL.
 *
 * Returns `null` for a no-op/out-of-range move so callers can skip the request.
 * Config keys are normalised to numeric strings (the update endpoint drops
 * URL-keyed legacy entries anyway) and a connection without config gets `{}`.
 * The input is never mutated.
 */
export const moveOpenAIConnectionOrder = (
	state: OpenAIOrderState,
	from: number,
	to: number
): OpenAIOrderState | null => {
	const urls = state?.urls ?? [];
	const count = urls.length;
	if (!Number.isInteger(from) || !Number.isInteger(to)) return null;
	if (from === to || from < 0 || to < 0 || from >= count || to >= count) return null;

	const order = urls.map((_, idx) => idx);
	const [moved] = order.splice(from, 1);
	order.splice(to, 0, moved);

	const keys = state.keys ?? [];
	const configs: Record<string, any> = {};
	order.forEach((oldIdx, newIdx) => {
		configs[String(newIdx)] = openAIConfigForIndex(state, oldIdx);
	});

	return {
		urls: order.map((oldIdx) => urls[oldIdx]),
		keys: order.map((oldIdx) => keys[oldIdx] ?? ''),
		configs
	};
};

/**
 * Add an OpenAI-compatible API connection at the system level.
 * Mirrors the logic in admin/Settings/Connections.svelte.
 */
export const addOpenAIConnection = async (
	token: string,
	connection: { url: string; key?: string; config?: object }
) => {
	const current = await getOpenAIConfig(token);
	const urls = current?.OPENAI_API_BASE_URLS ?? [];
	const keys = current?.OPENAI_API_KEYS ?? [];
	const configs = current?.OPENAI_API_CONFIGS ?? {};

	const normalizedUrl = connection.url.replace(/\/$/, '');

	// Don't add duplicates
	if (urls.map((u: string) => u.replace(/\/$/, '')).includes(normalizedUrl)) {
		return current;
	}

	urls.push(normalizedUrl);
	keys.push(connection.key ?? '');
	if (connection.config) {
		configs[(urls.length - 1).toString()] = connection.config;
	}

	return await updateOpenAIConfig(token, {
		ENABLE_OPENAI_API: current?.ENABLE_OPENAI_API ?? true,
		OPENAI_API_BASE_URLS: urls,
		OPENAI_API_KEYS: keys,
		OPENAI_API_CONFIGS: configs
	});
};

/**
 * Remove an OpenAI-compatible API connection by URL at the system level.
 * Re-indexes OPENAI_API_CONFIGS to match the admin delete pattern.
 */
export const removeOpenAIConnection = async (token: string, url: string) => {
	const current = await getOpenAIConfig(token);
	const urls: string[] = current?.OPENAI_API_BASE_URLS ?? [];
	const keys: string[] = current?.OPENAI_API_KEYS ?? [];
	const configs: Record<string, any> = current?.OPENAI_API_CONFIGS ?? {};

	const normalizedUrl = url.replace(/\/$/, '');
	const idx = urls.findIndex((u: string) => u.replace(/\/$/, '') === normalizedUrl);
	if (idx === -1) return current;

	const newUrls = urls.filter((_: string, i: number) => i !== idx);
	const newKeys = keys.filter((_: string, i: number) => i !== idx);

	// Re-index configs (mirrors admin/Settings/Connections.svelte onDelete)
	const newConfigs: Record<string, any> = {};
	newUrls.forEach((_: string, newIdx: number) => {
		newConfigs[newIdx] = configs[newIdx < idx ? newIdx : newIdx + 1];
	});

	return await updateOpenAIConfig(token, {
		ENABLE_OPENAI_API: current?.ENABLE_OPENAI_API ?? true,
		OPENAI_API_BASE_URLS: newUrls,
		OPENAI_API_KEYS: newKeys,
		OPENAI_API_CONFIGS: newConfigs
	});
};

// ─── Terminal Server Connections ────────────────────────

/**
 * Add a terminal server connection at the system level.
 * Mirrors the logic in admin/Settings/Integrations.svelte.
 */
export const addTerminalConnection = async (
	token: string,
	connection: { url: string; key?: string; name?: string; auth_type?: string }
) => {
	const current = await getTerminalServerConnections(token);
	const servers = current?.TERMINAL_SERVER_CONNECTIONS ?? [];

	// Don't add duplicates
	if (servers.find((s: any) => s.url === connection.url)) {
		return current;
	}

	servers.push({
		url: connection.url,
		key: connection.key ?? '',
		auth_type: connection.auth_type ?? 'bearer',
		name: connection.name ?? 'Open Terminal',
		enabled: true
	});

	return await setTerminalServerConnections(token, {
		TERMINAL_SERVER_CONNECTIONS: servers
	});
};

/**
 * Remove a terminal server connection by URL at the system level.
 */
export const removeTerminalConnection = async (token: string, url: string) => {
	const current = await getTerminalServerConnections(token);
	const servers = current?.TERMINAL_SERVER_CONNECTIONS ?? [];

	const filtered = servers.filter((s: any) => s.url !== url);
	if (filtered.length === servers.length) return current; // nothing to remove

	return await setTerminalServerConnections(token, {
		TERMINAL_SERVER_CONNECTIONS: filtered
	});
};
